import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiX, FiShield, FiKey, FiLock, FiSmartphone, FiDownload,
  FiLoader, FiTrash2, FiAlertTriangle, FiCheckCircle, FiRefreshCw
} from 'react-icons/fi';
import SafeIcon from './SafeIcon';
import { supabase } from '@/integrations/supabase/client';
import TwoFactorSetup from './TwoFactorSetup';

/**
 * $1B-grade Security modal.
 *
 * Terminal V3 aesthetic. Sections:
 *   1. Password        — change with strength meter
 *   2. Two-Factor Auth — Authenticator app (TOTP). SMS deprecated
 *                        industry-wide (SIM swap), not offered here.
 *   3. Recovery codes  — 10 one-time codes, download-as-txt UX.
 *                        Bounded to public.generate_recovery_codes().
 *   4. Danger zone     — Delete account (typed-email confirmation).
 *
 * Session management deliberately deferred: Supabase does not expose
 * a stable list-sessions API; building a shadow session table would
 * be a lot of code for a SaaS AI tool that does not custody funds.
 * Users can already force-logout everywhere by changing password.
 */

const SecurityModal = ({ isOpen, onClose, userEmail }) => {
  // 2FA
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [totpLoading, setTotpLoading] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);

  // Recovery codes
  const [recoveryCount, setRecoveryCount] = useState(null);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [freshCodes, setFreshCodes] = useState(null);  // shown once after generation

  // Password
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordStatus, setPasswordStatus] = useState(null);

  // Delete account
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // ── Hydrate on open ──────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    checkTotpStatus();
    checkRecoveryCount();
  }, [isOpen]);

  const checkTotpStatus = async () => {
    try {
      setTotpLoading(true);
      const { data } = await supabase.rpc('get_totp_status');
      if (data?.success) setTotpEnabled(!!data.enabled);
    } catch { /* silent */ } finally { setTotpLoading(false); }
  };

  const checkRecoveryCount = async () => {
    try {
      const { data } = await supabase.rpc('count_recovery_codes');
      if (data?.success) setRecoveryCount(data.remaining ?? 0);
    } catch { /* silent */ }
  };

  // ── 2FA handlers ─────────────────────────────────────────────────
  const handleDisable2FA = async () => {
    if (!confirm('Disable 2FA? Your account will be less secure.')) return;
    try {
      setTotpLoading(true);
      const { data } = await supabase.rpc('disable_totp');
      if (data?.success) setTotpEnabled(false);
    } finally { setTotpLoading(false); }
  };

  // ── Recovery codes handlers ──────────────────────────────────────
  const handleGenerateCodes = async () => {
    if (recoveryCount > 0 && !confirm('This will invalidate your existing recovery codes. Continue?')) return;
    try {
      setRecoveryLoading(true);
      const { data } = await supabase.rpc('generate_recovery_codes');
      if (data?.success) {
        setFreshCodes(data.codes);
        setRecoveryCount(data.codes.length);
      }
    } finally { setRecoveryLoading(false); }
  };

  const downloadCodes = () => {
    if (!freshCodes) return;
    const content = [
      'SNAP CHART AI — RECOVERY CODES',
      '',
      `Account: ${userEmail}`,
      `Generated: ${new Date().toISOString()}`,
      '',
      'Each code works ONCE. Keep them somewhere safe (password manager).',
      'You will use them if you lose access to your Authenticator app.',
      '',
      ...freshCodes.map((c, i) => `${String(i + 1).padStart(2, '0')}. ${c}`),
      '',
    ].join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `snapchart-recovery-codes-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Password change ──────────────────────────────────────────────
  const passwordStrength = (pw) => {
    if (!pw) return { score: 0, label: '' };
    let score = 0;
    if (pw.length >= 8) score++;
    if (pw.length >= 12) score++;
    if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
    if (/\d/.test(pw)) score++;
    if (/[^a-zA-Z0-9]/.test(pw)) score++;
    const labels = ['too short', 'weak', 'fair', 'good', 'strong', 'excellent'];
    return { score, label: labels[Math.min(score, 5)] };
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordStatus(null);

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordStatus({ type: 'error', message: 'New passwords do not match.' });
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      setPasswordStatus({ type: 'error', message: 'Password must be at least 8 characters.' });
      return;
    }

    setPasswordLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error: verifyError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: passwordForm.currentPassword,
      });
      if (verifyError) {
        setPasswordStatus({ type: 'error', message: 'Current password is incorrect.' });
        return;
      }
      const { error: updateError } = await supabase.auth.updateUser({ password: passwordForm.newPassword });
      if (updateError) {
        setPasswordStatus({ type: 'error', message: updateError.message });
        return;
      }
      setPasswordStatus({ type: 'success', message: 'Password updated.' });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => { setShowPasswordChange(false); setPasswordStatus(null); }, 1500);
    } catch {
      setPasswordStatus({ type: 'error', message: 'Unexpected error. Try again.' });
    } finally {
      setPasswordLoading(false);
    }
  };

  // ── Delete account ───────────────────────────────────────────────
  const handleDelete = async () => {
    setDeleteError(null);
    if (deleteConfirm.toLowerCase() !== (userEmail || '').toLowerCase()) {
      setDeleteError('Type your email exactly to confirm.');
      return;
    }
    setDeleteLoading(true);
    try {
      const { data, error } = await supabase.rpc('delete_my_account', { p_confirm_email: deleteConfirm });
      if (error || !data?.success) {
        setDeleteError(data?.error === 'EMAIL_MISMATCH' ? 'Email did not match.' : (error?.message || 'Deletion failed.'));
        return;
      }
      await supabase.auth.signOut();
      window.location.href = '/';
    } catch (e) {
      setDeleteError(e?.message || 'Deletion failed.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const pwStrength = passwordStrength(passwordForm.newPassword);
  const pwBarColor = ['bg-red-500', 'bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-green-500', 'bg-emerald-400'][pwStrength.score];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            className="bg-gray-950 border border-white/10 rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <div className="pointer-events-none absolute inset-0 opacity-[0.05] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:32px_32px]" />

              {/* Header */}
              <div className="relative px-6 pt-6 pb-4 flex items-start justify-between">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-2">
                    ▸ Security
                  </div>
                  <h3 className="text-2xl font-black text-white leading-tight">Protect your account.</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Two-factor auth, recovery codes, and account controls.
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 rounded-xl border border-white/10 text-gray-500 hover:text-white hover:border-white/20 transition-all"
                >
                  <SafeIcon icon={FiX} className="w-4 h-4" />
                </button>
              </div>

              <div className="relative px-6 pb-6 space-y-4">
                {/* ── PASSWORD ────────────────────────────────────────── */}
                <section className="rounded-2xl border border-white/10 bg-black/40 p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1">Authentication</div>
                      <div className="flex items-center gap-2">
                        <SafeIcon icon={FiKey} className="w-4 h-4 text-cyan-400" />
                        <h4 className="text-white font-bold">Password</h4>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">For account login.</p>
                    </div>
                    <button
                      onClick={() => setShowPasswordChange(v => !v)}
                      className="px-3 py-1.5 rounded-lg border border-white/10 text-white text-xs font-medium hover:bg-white/5 transition-colors"
                    >
                      {showPasswordChange ? 'Cancel' : 'Change'}
                    </button>
                  </div>

                  {showPasswordChange && (
                    <form onSubmit={handlePasswordChange} className="mt-4 space-y-3 pt-4 border-t border-white/5">
                      <input
                        type="password"
                        placeholder="Current password"
                        value={passwordForm.currentPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                        className="w-full bg-black/60 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-400/50"
                        required
                      />
                      <div>
                        <input
                          type="password"
                          placeholder="New password (min. 8 chars)"
                          value={passwordForm.newPassword}
                          onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                          className="w-full bg-black/60 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-400/50"
                          required
                          minLength={8}
                        />
                        {passwordForm.newPassword && (
                          <div className="mt-2 flex items-center gap-2">
                            <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
                              <div className={`h-full ${pwBarColor} transition-all`} style={{ width: `${(pwStrength.score / 5) * 100}%` }} />
                            </div>
                            <span className="font-mono text-[10px] uppercase text-gray-500">{pwStrength.label}</span>
                          </div>
                        )}
                      </div>
                      <input
                        type="password"
                        placeholder="Confirm new password"
                        value={passwordForm.confirmPassword}
                        onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                        className="w-full bg-black/60 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-400/50"
                        required
                      />
                      {passwordStatus && (
                        <div className={`text-xs font-mono ${passwordStatus.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                          {passwordStatus.type === 'success' ? '✓' : '✗'} {passwordStatus.message}
                        </div>
                      )}
                      <button
                        type="submit"
                        disabled={passwordLoading}
                        className="w-full py-2 rounded-lg bg-gradient-to-r from-cyan-400 to-purple-400 text-black text-sm font-bold hover:scale-[1.01] transition-transform disabled:opacity-50"
                      >
                        {passwordLoading ? <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin inline" /> : 'Update password'}
                      </button>
                    </form>
                  )}
                </section>

                {/* ── 2FA ────────────────────────────────────────────── */}
                <section className="rounded-2xl border border-white/10 bg-black/40 p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1">Two-factor authentication</div>
                      <div className="flex items-center gap-2">
                        <SafeIcon icon={FiSmartphone} className="w-4 h-4 text-cyan-400" />
                        <h4 className="text-white font-bold">Authenticator app</h4>
                        {totpEnabled && (
                          <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/10 border border-green-500/30 text-green-400 text-[10px] font-mono uppercase tracking-widest">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-400" /> Active
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Use Google Authenticator, 1Password, or Authy. SMS 2FA not offered — SIM-swap attacks are too common in 2026.
                      </p>
                    </div>
                    <button
                      onClick={totpEnabled ? handleDisable2FA : () => setShow2FASetup(true)}
                      disabled={totpLoading}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        totpEnabled
                          ? 'border border-red-500/30 text-red-300 hover:bg-red-500/10'
                          : 'bg-gradient-to-r from-cyan-400 to-purple-400 text-black font-bold hover:scale-[1.02]'
                      }`}
                    >
                      {totpLoading ? <SafeIcon icon={FiLoader} className="w-3.5 h-3.5 animate-spin" /> : (totpEnabled ? 'Disable' : 'Enable')}
                    </button>
                  </div>
                </section>

                {/* ── RECOVERY CODES ─────────────────────────────────── */}
                <section className="rounded-2xl border border-white/10 bg-black/40 p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1">Backup access</div>
                      <div className="flex items-center gap-2">
                        <SafeIcon icon={FiShield} className="w-4 h-4 text-cyan-400" />
                        <h4 className="text-white font-bold">Recovery codes</h4>
                        {recoveryCount !== null && recoveryCount > 0 && (
                          <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
                            {recoveryCount} left
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        10 one-time codes to log in if you lose your Authenticator app. Store them somewhere safe.
                      </p>
                    </div>
                    <button
                      onClick={handleGenerateCodes}
                      disabled={recoveryLoading}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 text-white text-xs font-medium hover:bg-white/5 transition-colors disabled:opacity-50"
                    >
                      {recoveryLoading
                        ? <SafeIcon icon={FiLoader} className="w-3.5 h-3.5 animate-spin" />
                        : <SafeIcon icon={FiRefreshCw} className="w-3.5 h-3.5" />}
                      {recoveryCount > 0 ? 'Regenerate' : 'Generate'}
                    </button>
                  </div>

                  {freshCodes && (
                    <div className="mt-4 pt-4 border-t border-white/5">
                      <div className="flex items-center gap-2 text-green-400 text-xs font-mono uppercase tracking-widest mb-3">
                        <SafeIcon icon={FiCheckCircle} className="w-3.5 h-3.5" />
                        Save these now — they will not be shown again
                      </div>
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        {freshCodes.map((code, i) => (
                          <div key={i} className="font-mono text-sm text-white bg-black/60 border border-white/10 rounded px-3 py-2 text-center">
                            {code}
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={downloadCodes}
                        className="w-full inline-flex items-center justify-center gap-2 py-2 rounded-lg bg-gradient-to-r from-cyan-400 to-purple-400 text-black text-sm font-bold hover:scale-[1.01] transition-transform"
                      >
                        <SafeIcon icon={FiDownload} className="w-4 h-4" />
                        Download as .txt
                      </button>
                    </div>
                  )}
                </section>

                {/* ── DANGER ZONE ────────────────────────────────────── */}
                <section className="rounded-2xl border border-red-500/20 bg-red-500/[0.03] p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-red-400 mb-1">Danger zone</div>
                      <div className="flex items-center gap-2">
                        <SafeIcon icon={FiTrash2} className="w-4 h-4 text-red-400" />
                        <h4 className="text-white font-bold">Delete account</h4>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Permanently wipe your account and all data. Cannot be undone.
                      </p>
                    </div>
                    {!showDelete && (
                      <button
                        onClick={() => setShowDelete(true)}
                        className="px-3 py-1.5 rounded-lg border border-red-500/30 text-red-300 text-xs font-medium hover:bg-red-500/10 transition-colors"
                      >
                        Delete…
                      </button>
                    )}
                  </div>

                  {showDelete && (
                    <div className="mt-4 pt-4 border-t border-red-500/10 space-y-3">
                      <div className="flex items-start gap-2 text-xs text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2.5">
                        <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>
                          This wipes your profile, journal entries, TDI history, invoices, and 2FA setup.
                          <br />
                          To confirm, type your email <span className="font-mono text-white">{userEmail}</span> below.
                        </span>
                      </div>
                      <input
                        type="email"
                        placeholder="Type your email to confirm"
                        value={deleteConfirm}
                        onChange={(e) => setDeleteConfirm(e.target.value)}
                        className="w-full bg-black/60 border border-red-500/30 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-red-500"
                      />
                      {deleteError && (
                        <div className="text-red-400 text-xs font-mono">✗ {deleteError}</div>
                      )}
                      <div className="flex gap-2">
                        <button
                          onClick={() => { setShowDelete(false); setDeleteConfirm(''); setDeleteError(null); }}
                          className="flex-1 py-2 rounded-lg border border-white/10 text-white text-sm hover:bg-white/5 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleDelete}
                          disabled={deleteLoading || deleteConfirm.toLowerCase() !== (userEmail || '').toLowerCase()}
                          className="flex-1 py-2 rounded-lg bg-red-500 text-white text-sm font-bold hover:bg-red-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {deleteLoading ? <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin mx-auto" /> : 'Permanently delete account'}
                        </button>
                      </div>
                    </div>
                  )}
                </section>
              </div>
            </div>
          </motion.div>

          {/* Nested 2FA setup sub-modal (unchanged component) */}
          {show2FASetup && (
            <TwoFactorSetup
              isOpen={show2FASetup}
              onClose={() => { setShow2FASetup(false); checkTotpStatus(); checkRecoveryCount(); }}
              onSuccess={() => { setTotpEnabled(true); checkRecoveryCount(); }}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SecurityModal;
